import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BadgeCheck, Star } from 'lucide-react';
import { request, fmtDate, fmtNum } from '@/lib/api';
import { Pager, PAGE_SIZE } from '@/components/Pager';
import { RefreshButton } from '@/components/RefreshButton';

interface UserRow {
  id: string;
  email: string;
  full_name: string | null;
  role: string;
  is_pro: boolean;
  email_verified: boolean;
  linked_google: boolean;
  created_at: string;
  points_count: number;
  comments_count: number;
}

// Logo "G" de Google (lucide no incluye marcas). Tamaño alineado con los demás
// badges (BadgeCheck/Star) de la fila.
function GoogleGlyph({ label }: { label: string }) {
  return (
    <svg width={14} height={14} viewBox="0 0 48 48" role="img" aria-label={label}>
      <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z" />
      <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z" />
      <path fill="#FBBC05" d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34A21.99 21.99 0 0 0 2 24c0 3.55.85 6.91 2.34 9.88l7.35-5.7z" />
      <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z" />
    </svg>
  );
}

export function UsersPage() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState<{ rows: UserRow[]; total: number } | null>(null);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
    if (search) params.set('search', search);
    const timer = setTimeout(() => {
      request<{ rows: UserRow[]; total: number }>(`/backoffice/users?${params}`)
        .then(setData)
        .catch(() => setError(true));
    }, 250); // debounce de la búsqueda
    return () => clearTimeout(timer);
  }, [search, offset, reloadKey]);

  const refresh = () => setReloadKey((k) => k + 1);

  if (error) return <p className="text-sm text-red-600">{t('common.error')}</p>;

  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-2xl font-semibold">
          {t('users.title')}
          {data && <span className="ml-2 text-base font-normal text-stone-400">{fmtNum(data.total)}</span>}
        </h1>
        <RefreshButton onClick={refresh} />
      </div>

      <input
        type="search"
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setOffset(0);
        }}
        placeholder={t('users.searchPlaceholder')}
        className="mt-4 w-full max-w-md rounded-md border border-stone-300 bg-white px-3 py-2 text-sm focus:border-amber-600 focus:outline-none"
      />

      {!data ? (
        <p className="mt-6 text-sm text-stone-500">{t('common.loading')}</p>
      ) : data.rows.length === 0 ? (
        <p className="mt-6 text-sm text-stone-500">{t('common.empty')}</p>
      ) : (
        <>
          <div className="mt-4 overflow-x-auto rounded-xl bg-white shadow-sm">
            <table className="w-full min-w-[640px] text-left text-sm">
              <thead className="border-b border-stone-200 text-xs uppercase text-stone-500">
                <tr>
                  <th className="px-4 py-3">{t('users.email')}</th>
                  <th className="px-4 py-3">{t('users.name')}</th>
                  <th className="px-4 py-3 text-right">{t('users.points')}</th>
                  <th className="px-4 py-3 text-right">{t('users.comments')}</th>
                  <th className="px-4 py-3">{t('users.registered')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {data.rows.map((u) => (
                  <tr key={u.id} className="hover:bg-stone-50">
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1.5">
                        {u.email}
                        {u.email_verified && (
                          <BadgeCheck size={14} className="text-emerald-600" aria-label={t('users.verified')} />
                        )}
                        {u.is_pro && (
                          <Star size={14} className="text-amber-500" aria-label={t('users.pro')} />
                        )}
                        {u.linked_google && <GoogleGlyph label={t('users.google')} />}
                        {u.role === 'admin' && (
                          <span className="rounded bg-stone-800 px-1.5 py-0.5 text-[10px] font-medium uppercase text-white">
                            admin
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-stone-600">{u.full_name || '—'}</td>
                    <td className="px-4 py-3 text-right">{fmtNum(u.points_count)}</td>
                    <td className="px-4 py-3 text-right">{fmtNum(u.comments_count)}</td>
                    <td className="px-4 py-3 text-stone-600">{fmtDate(u.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager offset={offset} total={data.total} onChange={setOffset} />
        </>
      )}
    </div>
  );
}
